import { useState } from "react";
import { Checkbox, Col, Form, Input, Row, Typography } from "antd";
import { AttachFileButton, Box, Button, MicrosoftPeopleSelect, ViagsUpload } from "../../../../../../components";
import BoxSectionItem from "../../../../../../components/box-section/box-section-item";
import {
  EnvKey,
  RegExp,
  RequestStatus,
  RequestType,
  Rules,
  SystemCode,
  TitleCode,
  titleGroups,
} from "../../../../../../constants";
import { DateHelper, EnvHelper, StringHelper } from "../../../../../../helper";
import VacationPeriodList from "../vacation-period-list";
import { useHistory, useLocation } from "react-router-dom";
import { FormText } from "../form-text";
import { useSearchViagsUser } from "../../hooks/use-search-viags-user";
import { IViagsLeaveInfo } from "../../../../../../services/viags/application-for-leave.service";
import { useAFLSteps, useCreateAFL, useUpdateAFL } from "../../hooks";
import { useViagsUpload } from "../../hooks/use-viags-upload";
import { AFLType } from "../../models";
import { UserHelper } from "../../utils";

import omit from "lodash/omit";
interface Props {
  data?: any;
}
export const checkValidDates = (eVacationPeriods: any, paidLeaveRemain: number) => {
  if (!eVacationPeriods || eVacationPeriods.some((i: any) => !i)) {
    return false;
  }

  if (!paidLeaveRemain || paidLeaveRemain === 0) return false;

  return DateHelper.multipleRangeDateToDays(eVacationPeriods) <= paidLeaveRemain;
};

export const CreateApplicationForLeaveForm = ({ data = {} }: Props) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [searchedUser, setSearchedUser] = useState({} as IViagsLeaveInfo);
  const { pathname } = useLocation();

  const { searchViagsUser, isLoading: isSearching } = useSearchViagsUser();
  const { createApplicationForLeave } = useCreateAFL();
  const { updateApplicationForLeave } = useUpdateAFL();
  const { mapUploadResponse } = useViagsUpload();
  const { getAvailableStep, notify, getSteps } = useAFLSteps();

  const siteUrl = EnvHelper.getValue(
    pathname.includes("nba")
      ? EnvKey.APPROVER_AFL_NBA_URL
      : pathname.includes("dad")
      ? EnvKey.APPROVER_AFL_DAD_URL
      : EnvKey.APPROVER_AFL_URL,
  );

  const isCreateSession = !data?.id;

  const eTitle = Form.useWatch("eTitle", form);
  const eVacationPeriods = Form.useWatch("eVacationPeriods", form);

  const ePaidLeaveRemain = Form.useWatch("ePaidLeaveRemain", form);
  const isValidDates = checkValidDates(eVacationPeriods, isCreateSession ? ePaidLeaveRemain : data?.ePaidLeaveRemain);

  const daysOffNum =
    eVacationPeriods && !eVacationPeriods.some((i: any) => Boolean(!i))
      ? DateHelper.duration(eVacationPeriods!).days()
      : 0;

  const [titleCode] = StringHelper.verticalBarSplit(eTitle ?? StringHelper.verticalBarSplit(data?.eTitle)[0]);

  const isKCQ = searchedUser?.listDepartment?.[0]?.code === "KCQ";
  const isTSN = ["TSN", "VTSN"].includes(searchedUser?.listDepartment?.[0]?.code);
  const isNBA = ["NBA", "VNBA"].includes(searchedUser?.listDepartment?.[0]?.code);
  const isDAD = ["DAD", "VDAD"].includes(searchedUser?.listDepartment?.[0]?.code);
  const isTitleGroup1 = UserHelper.inGroup(titleCode, titleGroups.one);
  const isTitleGroup2 = UserHelper.inGroup(titleCode, titleGroups.two);
  const isTitleGroup3 = UserHelper.inGroup(titleCode, titleGroups.three);
  const isTitleGroup4 = UserHelper.inGroup(titleCode, titleGroups.four);
  const isTitleGroup5 = UserHelper.inGroup(titleCode, titleGroups.five);
  const isTitleGroup6 = UserHelper.inGroup(titleCode, titleGroups.six);
  const isTitleGroup7 = UserHelper.inGroup(titleCode, titleGroups.seven);
  const isTitleGroup8 = UserHelper.inGroup(titleCode, titleGroups.eight);
  const isDirector = UserHelper.inGroup(titleCode, [TitleCode.GD]);

  const onSearch = (employeeId: string) => {
    searchViagsUser(
      { employeeId },
      {
        onSuccess(response) {
          const [userInfo] = response;

          setSearchedUser(userInfo);
          form.setFieldsValue({
            ...UserHelper.getDepartmentInfo(userInfo),
            eRequestName: userInfo.fullName,
            ePaidLeaveRemain: userInfo.paidLeave_Remain_Early,
            ePhone: userInfo.mobile_1 || userInfo.mobile_2,
            eTitle: `${userInfo.code_Job_Title}|${userInfo.jobTitle_NameVN}`,
          });
        },
      },
    );
  };

  const onSubmit = (values: any) => {
    const { initialize } = getAvailableStep(SystemCode.AFL, values.eApprovedBy);
    values["eAttachments"] = mapUploadResponse(values.eAttachments);

    const departmentCode = searchedUser.listDepartment[0].code;
    const eBranch = `${departmentCode}|${UserHelper.getBranchTitle(departmentCode!)}`;

    if (!isKCQ && !isTSN && !isNBA && !isDAD) return;

    values["eSteps"] = getSteps({
      branchCode: departmentCode,
      dayOffNumber: daysOffNum,
      initialize,
      titleCode,
      unitCode: searchedUser.listDepartment[1].code,
      eApproverBy: values.eApprovedBy,
      eTeamLead: values.eTeamLead,
      isOverseas: values.isOverseas,
    });

    values["eApprovalProcess"] = { current: 0, status: "finish" };

    const body = {
      ...omit(values, ["eTeamLead", "eDirector"]),
      isOverseas: !!values.isOverseas,
      Title: "Đơn xin nghỉ phép",
      eAFLType: `${AFLType.ApplicationForLeave}|Đơn xin nghỉ phép`,
      eRequestStatus: RequestStatus.submitted,
      eRequestType: RequestType.confirm,
      eBranch,
    };

    createApplicationForLeave(body, {
      onSuccess(response: any) {
        const { id, eRequestStatus, eRequestName, eEmail } = response.fields;
        const url = pathname.includes("nba")
          ? `/procedure/absence-letter-nba/${response.id}`
          : pathname.includes("dad")
          ? `/procedure/absence-letter-dad/${response.id}`
          : `/procedure/absence-letter/${response.id}`;

        history.push(url);
        notify({
          steps: JSON.parse(response.fields.eSteps),
          toPath: `${siteUrl}/${id}`,
          eRequestStatus,
          current: JSON.parse(response.fields.eApprovalProcess).current,
          requestUser: { name: eRequestName, email: eEmail },
          reason: "",
        });
      },
    });
  };

  const onEdit = (values: any) => {
    values["eAttachments"] = mapUploadResponse(values.eAttachments);
    updateApplicationForLeave(
      { id: data!.id, body: values },
      {
        successText: "Chỉnh sửa yêu cầu thành công.",
        errorText: "Chỉnh sửa yêu cầu thất bại.",
        onSuccess(response: any) {
          const url = pathname.includes("nba")
            ? `/procedure/absence-letter-nba/${response.id}`
            : pathname.includes("dad")
            ? `/procedure/absence-letter-dad/${response.id}`
            : `/procedure/absence-letter/${response.id}`;

          history.push(url);
        },
      },
    );
  };

  const isEmployeesSelect1 = (isKCQ && !isTitleGroup1 && !isTitleGroup2) || (isTSN && !isTitleGroup4 && !isTitleGroup7);
  const isEmployeesSelect2 =
    (isKCQ && !isTitleGroup1 && !isTitleGroup2 && !isTitleGroup3) ||
    (isTSN && !isTitleGroup4) ||
    (isTSN && isTitleGroup7 && !isTitleGroup6);

  const isNBAEmployeeSelect = isNBA && (isTitleGroup3 || isTitleGroup5);

  const isDADTeamLeadSelect = isDAD && (isTitleGroup5 || isTitleGroup6);

  return (
    <Form
      form={form}
      onFinish={isCreateSession ? onSubmit : onEdit}
      size="large"
      layout="vertical"
      initialValues={{
        eVacationPeriods: [null],
        eAttachments: [],
        ...data,
      }}
    >
      <Box title="Thông tin">
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Mã nhân viên" name="eEmployeeId" rules={[Rules.required()]}>
                <Input.Search loading={isSearching} onSearch={onSearch} />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Mã nhân viên" textRight={data?.eEmployeeId} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Họ tên" name="eRequestName" rules={[Rules.required()]}>
                <Input disabled />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Họ tên" textRight={data?.eRequestName} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item
                label={
                  <Typography.Text ellipsis={{ tooltip: "Số ngày phép còn lại trong năm" }}>
                    Số ngày phép còn lại trong năm
                  </Typography.Text>
                }
                name="ePaidLeaveRemain"
              >
                <Input disabled />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Số ngày phép còn lại trong năm" textRight={data?.ePaidLeaveRemain} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Chức danh" name="eTitle">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem
                ellipsis
                textLeft="Chức danh"
                textRight={StringHelper.verticalBarSplit(data?.eTitle)[1]}
              />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Cơ quan/Đơn vị" name="eDepartment">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem
                ellipsis
                textLeft="Cơ quan/Đơn vị"
                textRight={StringHelper.verticalBarSplit(data?.eDepartment)[1]}
              />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Đơn vị" name="eUnit">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Đơn vị" textRight={StringHelper.verticalBarSplit(data?.eUnit)[1]} />
            )}
          </Col>
          <Col xs={24} md={12}>
            {isCreateSession ? (
              <Form.Item label="Đội/Nhóm" name="eTeam">
                <FormText />
              </Form.Item>
            ) : (
              <BoxSectionItem ellipsis textLeft="Đội/Nhóm" textRight={StringHelper.verticalBarSplit(data?.eTeam)[1]} />
            )}
          </Col>
        </Row>
      </Box>
      <Box title="Thông tin chi tiết">
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item name="eAttachments" valuePropName="fileList" getValueFromEvent={(e) => e.fileList}>
              <ViagsUpload>
                <AttachFileButton />
              </ViagsUpload>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item required label="Thời gian xin nghỉ phép" style={{ marginBottom: 0 }}>
              <VacationPeriodList
                disabled={!isCreateSession}
                ePaidLeaveRemain={isCreateSession ? ePaidLeaveRemain : data?.ePaidLeaveRemain}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Form.Item
              label={
                <Typography.Text
                  ellipsis={{
                    tooltip: "Nghỉ phép lần thứ bao nhiêu trong năm",
                  }}
                >
                  Nghỉ phép lần thứ bao nhiêu trong năm
                </Typography.Text>
              }
              name="ePaidLeaveTimes"
            >
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Form.Item
              label="Email"
              name="eEmail"
              rules={[{ pattern: RegExp.email, message: "Định dạng phải là email" }]}
            >
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item label="Nơi nghỉ phép" name="eLeavePlace" rules={[Rules.required("Vui lòng nhập trường này!")]}>
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} md={12}>
            <Form.Item
              label="Số điện thoại liên lạc"
              name="ePhone"
              rules={[
                Rules.required("Vui lòng nhập trường này!"),
                {
                  pattern: RegExp.vnPhoneNumber,
                  message: "Định dạng phải là số điện thoại!",
                },
              ]}
            >
              <Input disabled={!isValidDates} />
            </Form.Item>
          </Col>
          {((isTitleGroup2 && !isNBA) || isDirector || (isDAD && isTitleGroup8) || data?.eProxyId) && (
            <>
              <Col span={12}>
                <Form.Item label="Giấy ủy quyền số" name="eProxyId" rules={[Rules.required()]}>
                  <Input disabled={!isValidDates} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Ủy quyền cho" name="eProxyFor" rules={[Rules.required()]}>
                  <Input disabled={!isValidDates} />
                </Form.Item>
              </Col>
            </>
          )}
          <Col span={24}>
            <Form.Item label="Lý do" name="eReason" rules={[Rules.required()]}>
              <Input.TextArea disabled={!isValidDates} />
            </Form.Item>
          </Col>
        </Row>
        {isCreateSession && (
          <>
            {isEmployeesSelect2 && (
              <Form.Item
                label="Chọn Đội/Nhóm trưởng"
                name="eTeamLead"
                rules={[Rules.required("Vui lòng chọn trường này")]}
              >
                <MicrosoftPeopleSelect disabled={!isValidDates} placeholder="Đội/Nhóm trưởng" />
              </Form.Item>
            )}
            {isEmployeesSelect1 && (
              <Form.Item
                label="Chọn Thủ trưởng CQ/ĐV"
                name="eApprovedBy"
                rules={[Rules.required("Vui lòng chọn trường này")]}
              >
                <MicrosoftPeopleSelect disabled={!isValidDates} />
              </Form.Item>
            )}
            {isNBAEmployeeSelect && (
              <Form.Item
                label="Chọn Trưởng phòng/Trung tâm"
                name="eApprovedBy"
                rules={[Rules.required("Vui lòng chọn trường này")]}
              >
                <MicrosoftPeopleSelect disabled={!isValidDates} />
              </Form.Item>
            )}
            {isNBA && !isTitleGroup8 && (
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    label="Chọn Đội/Nhóm trưởng"
                    name="eTeamLead"
                    rules={[Rules.required("Vui lòng chọn trường này")]}
                  >
                    <MicrosoftPeopleSelect disabled={!isValidDates} placeholder="Đội/Nhóm trưởng" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Chọn Trưởng phòng/Trung tâm"
                    name="eApprovedBy"
                    rules={[Rules.required("Vui lòng chọn trường này")]}
                  >
                    <MicrosoftPeopleSelect disabled={!isValidDates} />
                  </Form.Item>
                </Col>
              </Row>
            )}
            {isDADTeamLeadSelect && (
              <Form.Item
                label="Chọn Thủ trưởng CQ/ĐV"
                name="eApprovedBy"
                rules={[Rules.required("Vui lòng chọn trường này")]}
              >
                <MicrosoftPeopleSelect disabled={!isValidDates} />
              </Form.Item>
            )}
            {isDAD && !isTitleGroup8 && (
              <>
                <Form.Item noStyle name="isOverseas" valuePropName="checked">
                  <Checkbox disabled={!isValidDates}>
                    <span className="text-base">Nghỉ phép ngoài nước</span>
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  label="Chọn Thủ trưởng CQ/ĐV"
                  name="eApprovedBy"
                  rules={[Rules.required("Vui lòng chọn trường này")]}
                >
                  <MicrosoftPeopleSelect disabled={!isValidDates} />
                </Form.Item>
              </>
            )}
          </>
        )}
      </Box>
      <div className="gap-10 flex-end">
        <Button disabled={!isValidDates} size="lg" style={{ minWidth: 120 }} variant="danger" onClick={history.goBack}>
          Huỷ
        </Button>
        <Button disabled={!isValidDates} size="lg" type="submit" style={{ minWidth: 120 }} variant="confirm">
          Xác nhận
        </Button>
      </div>
    </Form>
  );
};
